import { useMemo, useState } from 'react';
import {
  Workflow, Search, ChevronDown, Clock, Webhook, Bell, PlayCircle, FileCode2, X, CalendarCheck,
  PauseCircle, Power, Loader2, AlertTriangle,
} from 'lucide-react';
import { classNames } from '@/lib/utils';
import { useApi } from '@/hooks/useApi';
import { api, type EstadoInterruptor } from '@/lib/api';
import Modal from '@/components/ui/Modal';
import {
  AUTOMATIONS, CATEGORY_LABELS, CATEGORY_STYLES, INFRA,
  LAST_REVIEWED, LAST_REVIEWED_COMMIT, formatReviewDate,
  type Automation, type AutomationCategory,
} from '@/data/automations';

const CATEGORIES = Object.keys(CATEGORY_LABELS) as AutomationCategory[];

const ENCENDIDA: EstadoInterruptor = { enabled: true };

function matches(a: Automation, q: string): boolean {
  if (!q) return true;
  const haystack = [
    a.id, a.name, a.summary, a.service, a.when, a.cron ?? '', a.endpoint ?? '',
    a.notifies ?? '', a.whenOff ?? '', ...a.detail, ...a.integrations,
    ...a.params.map(p => `${p.label} ${p.value}`),
  ].join(' ').toLowerCase();
  return haystack.includes(q.toLowerCase());
}

function fechaHora(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-ES', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid',
  });
}

/** Qué impide mover el interruptor ahora mismo, o null si se puede. */
type Bloqueo = string | null;

function Interruptor({
  automation, estado, guardando, bloqueo, onCambiar,
}: {
  automation: Automation;
  estado: EstadoInterruptor;
  guardando: boolean;
  bloqueo: Bloqueo;
  onCambiar: () => void;
}) {
  const encendida = estado.enabled;
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        role="switch"
        aria-checked={encendida}
        aria-label={`${encendida ? 'Apagar' : 'Encender'} ${automation.name}`}
        title={bloqueo ?? (encendida ? 'Encendida: pulsa para apagarla' : 'Apagada: pulsa para encenderla')}
        onClick={onCambiar}
        disabled={guardando || bloqueo !== null}
        className={classNames(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          encendida ? 'bg-green-600' : 'bg-gray-300',
        )}
      >
        <span
          className={classNames(
            'inline-flex h-5 w-5 items-center justify-center transform rounded-full bg-white shadow transition-transform',
            encendida ? 'translate-x-6' : 'translate-x-1',
          )}
        >
          {guardando && <Loader2 size={12} className="animate-spin text-gray-500" />}
        </span>
      </button>
      <span className={classNames('text-[11px] font-medium', encendida ? 'text-green-700' : 'text-amber-700')}>
        {encendida ? 'Encendida' : 'Apagada'}
      </span>
    </div>
  );
}

function AutomationCard({
  automation, estado, guardando, bloqueo, errorCambio, onCambiar,
}: {
  automation: Automation;
  estado: EstadoInterruptor;
  guardando: boolean;
  bloqueo: Bloqueo;
  errorCambio?: string;
  onCambiar: () => void;
}) {
  const [open, setOpen] = useState(false);
  const style = CATEGORY_STYLES[automation.category];
  const isTimer = automation.kind === 'timer';
  const conInterruptor = !automation.noSwitchReason;
  const apagada = conInterruptor && !estado.enabled;
  const cuando = fechaHora(estado.updatedAt);

  return (
    <div className={classNames(
      'bg-white rounded-2xl border shadow-sm overflow-hidden',
      apagada ? 'border-amber-300' : 'border-slate-200',
    )}>
      <div className="flex items-start">
        <button
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          className="min-w-0 flex-1 text-left p-4 pr-2 sm:p-5 sm:pr-3 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 hover:bg-slate-50/70 transition-colors"
        >
          {/* En móvil, una línea encima del texto; desde sm, la píldora de siempre a la izquierda */}
          <div className={classNames(
            'shrink-0 rounded-xl text-white text-center px-2.5 py-1 flex items-center gap-1.5',
            'sm:block sm:w-28 sm:px-2 sm:py-2.5',
            apagada ? 'bg-gray-400' : style.pill,
          )}>
            <div className="flex items-center justify-center gap-1 text-[11px] uppercase tracking-wide opacity-80">
              {isTimer ? <Clock size={11} /> : <Webhook size={11} />}
              {isTimer ? 'Timer' : 'HTTP'}
            </div>
            <div className="text-xs font-semibold leading-tight sm:mt-0.5">{automation.when}</div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={classNames('font-semibold', apagada ? 'text-slate-500' : 'text-sereno-dark')}>
                {automation.name}
              </h3>
              <span className={classNames('text-xs font-medium px-2 py-0.5 rounded-full border', style.chip)}>
                {CATEGORY_LABELS[automation.category]}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{automation.summary}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
              {automation.integrations.map(i => (
                <span key={i} className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                  {i}
                </span>
              ))}
            </div>
          </div>

          <ChevronDown
            size={18}
            className={classNames(
              'hidden sm:block shrink-0 mt-1 text-slate-400 transition-transform duration-200',
              open ? 'rotate-180' : '',
            )}
          />
        </button>

        <div className="shrink-0 w-16 sm:w-20 pt-4 pr-3 sm:pt-5 sm:pr-4 flex justify-center">
          {conInterruptor ? (
            <Interruptor
              automation={automation}
              estado={estado}
              guardando={guardando}
              bloqueo={bloqueo}
              onCambiar={onCambiar}
            />
          ) : (
            <span
              className="text-[11px] leading-tight text-center text-slate-400"
              title={automation.noSwitchReason}
            >
              Sin interruptor
            </span>
          )}
        </div>
      </div>

      {apagada && (
        <div className="mx-4 sm:mx-5 mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <PauseCircle size={14} className="mt-px shrink-0" />
          <span>
            Apagada desde el CRM{estado.updatedBy ? ` por ${estado.updatedBy}` : ''}{cuando ? ` el ${cuando}` : ''}.
            {' '}No se ejecuta hasta que alguien la vuelva a encender.
          </span>
        </div>
      )}

      {errorCambio && (
        <div className="mx-4 sm:mx-5 mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle size={14} className="mt-px shrink-0" />
          <span>No se pudo cambiar: {errorCambio}</span>
        </div>
      )}

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 pt-4">
            <span className="font-mono text-slate-700">{automation.id}</span>
            {automation.cron && (
              <span>
                NCRONTAB <code className="font-mono text-slate-700">{automation.cron}</code> (hora Madrid)
              </span>
            )}
            {automation.endpoint && (
              <span className="font-mono text-slate-700">{automation.endpoint}</span>
            )}
          </div>

          <ul className="space-y-2">
            {automation.detail.map((d, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                <span className={classNames('mt-1.5 h-1.5 w-1.5 rounded-full shrink-0', CATEGORY_STYLES[automation.category].dot)} />
                <span>{d}</span>
              </li>
            ))}
          </ul>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Parámetros que la gobiernan
            </h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {automation.params.map(p => (
                <div key={p.label} className="text-sm border-l-2 border-slate-100 pl-3">
                  <dt className="text-slate-500">{p.label}</dt>
                  <dd className="font-mono text-[13px] text-slate-800 break-words">{p.value}</dd>
                  {p.note && <dd className="text-xs text-slate-400 mt-0.5">{p.note}</dd>}
                </div>
              ))}
            </dl>
          </div>

          <div className="space-y-1.5 text-sm text-slate-600">
            <div className="flex items-start gap-2">
              <FileCode2 size={15} className="mt-0.5 text-slate-400 shrink-0" />
              <span className="font-mono text-[13px] break-all">{automation.service}</span>
            </div>
            {automation.notifies && (
              <div className="flex items-start gap-2">
                <Bell size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <span>{automation.notifies}</span>
              </div>
            )}
            {automation.manualRun && (
              <div className="flex items-start gap-2">
                <PlayCircle size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <span className="break-words">{automation.manualRun}</span>
              </div>
            )}
            {automation.whenOff && (
              <div className="flex items-start gap-2">
                <Power size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <span><span className="font-medium">Si se apaga:</span> {automation.whenOff}</span>
              </div>
            )}
            {automation.noSwitchReason && (
              <div className="flex items-start gap-2">
                <Power size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <span><span className="font-medium">Sin interruptor:</span> {automation.noSwitchReason}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AutomatizacionesPage() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<AutomationCategory | 'todas'>('todas');
  const [kind, setKind] = useState<'todos' | 'timer' | 'http'>('todos');
  const [soloApagadas, setSoloApagadas] = useState(false);

  const interruptores = useApi(() => api.getInterruptores(), []);
  // Lo guardado en esta visita, encima de lo leído: así no hace falta recargar
  // la lista entera tras cada cambio.
  const [cambios, setCambios] = useState<Record<string, EstadoInterruptor>>({});
  const [guardando, setGuardando] = useState<Record<string, boolean>>({});
  const [erroresCambio, setErroresCambio] = useState<Record<string, string>>({});
  const [confirmando, setConfirmando] = useState<Automation | null>(null);

  const estadoDe = (id: string): EstadoInterruptor =>
    cambios[id] ?? interruptores.data?.estados[id] ?? ENCENDIDA;

  const bloqueoDe = (id: string): Bloqueo => {
    if (interruptores.loading && !interruptores.data) return 'Cargando el estado…';
    if (interruptores.error) return 'No se pudo leer el estado de los interruptores';
    if (!interruptores.data?.disponible) return 'Los interruptores aún no están listos (ver aviso arriba)';
    if (!interruptores.data.conInterruptor.includes(id)) return 'El servidor no reconoce esta automatización';
    return null;
  };

  const cambiar = async (a: Automation, enabled: boolean) => {
    setGuardando(g => ({ ...g, [a.id]: true }));
    setErroresCambio(errs => {
      const resto = { ...errs };
      delete resto[a.id];
      return resto;
    });
    try {
      const guardado = await api.cambiarInterruptor(a.id, enabled);
      setCambios(c => ({ ...c, [a.id]: guardado }));
    } catch (e) {
      setErroresCambio(errs => ({ ...errs, [a.id]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setGuardando(g => ({ ...g, [a.id]: false }));
    }
  };

  // Apagar se confirma, porque puede dejar sin cobrar o sin llave a alguien;
  // encender vuelve al estado normal y va directo.
  const alPulsar = (a: Automation) => {
    if (estadoDe(a.id).enabled) setConfirmando(a);
    else void cambiar(a, true);
  };

  const timers = AUTOMATIONS.filter(a => a.kind === 'timer').length;
  const endpoints = AUTOMATIONS.length - timers;
  const apagadas = AUTOMATIONS.filter(a => !a.noSwitchReason && !estadoDe(a.id).enabled);

  const visible = useMemo(() => {
    return AUTOMATIONS
      .filter(a => category === 'todas' || a.category === category)
      .filter(a => kind === 'todos' || a.kind === kind)
      .filter(a => !soloApagadas || (!a.noSwitchReason && !estadoDe(a.id).enabled))
      .filter(a => matches(a, query))
      .sort((a, b) => a.sortKey - b.sortKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, category, kind, soloApagadas, interruptores.data, cambios]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <div className="bg-sereno-dark p-2.5 rounded-xl text-white">
            <Workflow size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-sereno-dark">Automatizaciones</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {timers} tareas programadas y {endpoints} endpoints bajo demanda en la Function App{' '}
              <span className="font-mono">{INFRA.functionApp}</span>.
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded-full pl-2.5 pr-3 py-1">
            <CalendarCheck size={13} className="text-slate-400" />
            <span>
              Datos revisados el <strong className="font-semibold text-slate-700">{formatReviewDate(LAST_REVIEWED)}</strong>
              {' · '}
              <span className="font-mono">{INFRA.repo}@{LAST_REVIEWED_COMMIT}</span>
            </span>
          </div>
          {interruptores.data?.disponible && (
            apagadas.length > 0 ? (
              <button
                onClick={() => setSoloApagadas(s => !s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-3 py-1 hover:bg-amber-100"
              >
                <PauseCircle size={13} />
                {apagadas.length === 1 ? '1 apagada' : `${apagadas.length} apagadas`}
                {soloApagadas ? ' · ver todas' : ''}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                <Power size={13} />
                Todas encendidas
              </span>
            )
          )}
        </div>
      </div>

      {interruptores.error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            No se pudo leer qué automatizaciones están encendidas: {interruptores.error}.{' '}
            <button onClick={interruptores.refresh} className="underline font-medium">Reintentar</button>
          </div>
        </div>
      )}
      {interruptores.data && !interruptores.data.disponible && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            Los interruptores aún no están listos: el contenedor donde se guardan lo crea{' '}
            <span className="font-mono">{INFRA.functionApp}</span> la primera vez que arranca con la versión que
            los respeta. Hasta entonces todas siguen encendidas, como siempre.
          </div>
        </div>
      )}

      {/* Infraestructura */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Dónde vive</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          {[
            ['Repositorio', INFRA.repo],
            ['Function App', INFRA.functionApp],
            ['Resource group', INFRA.resourceGroup],
            ['Runtime', INFRA.runtime],
            ['Zona horaria', INFRA.timezone],
            ['Secretos', INFRA.keyVault],
            ['Interruptores', INFRA.switches],
            ['Alertas de fallo', INFRA.alerts],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-slate-500 text-xs">{label}</dt>
              <dd className="font-mono text-[13px] text-slate-800 break-words">{value}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-slate-400 mt-4 leading-relaxed">
          Los horarios son hora de Madrid: la Function App tiene <span className="font-mono">WEBSITE_TIME_ZONE=Europe/Madrid</span>{' '}
          y el NCRONTAB se interpreta ya en local, sin convertir a UTC.
        </p>
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
          Apagar una automatización vale desde su siguiente ejecución, sin desplegar nada: la función se sigue
          disparando a su hora, pero sale sin hacer nada, y un endpoint apagado responde{' '}
          <span className="font-mono">{'{"status":"disabled"}'}</span>. Cada cambio se avisa en el topic Sistemas de
          Telegram. Si el cron no puede leer el interruptor, la ejecuta igualmente.
        </p>
      </div>

      {/* Filtros */}
      <div className="space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar por nombre, servicio, parámetro, integración…"
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Limpiar búsqueda"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setCategory('todas')}
            className={classNames(
              'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
              category === 'todas'
                ? 'bg-sereno-dark text-white border-sereno-dark'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
            )}
          >
            Todas ({AUTOMATIONS.length})
          </button>
          {CATEGORIES.map(c => {
            const count = AUTOMATIONS.filter(a => a.category === c).length;
            const active = category === c;
            return (
              <button
                key={c}
                onClick={() => setCategory(active ? 'todas' : c)}
                className={classNames(
                  'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                  active ? CATEGORY_STYLES[c].chip : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                )}
              >
                {CATEGORY_LABELS[c]} ({count})
              </button>
            );
          })}
          <span className="w-px bg-slate-200 mx-1" />
          {([['timer', 'Programadas'], ['http', 'Bajo demanda']] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setKind(kind === k ? 'todos' : k)}
              className={classNames(
                'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                kind === k
                  ? 'bg-sereno-dark text-white border-sereno-dark'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
              )}
            >
              {label}
            </button>
          ))}
          {(apagadas.length > 0 || soloApagadas) && (
            <button
              onClick={() => setSoloApagadas(s => !s)}
              className={classNames(
                'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                soloApagadas
                  ? 'bg-amber-50 text-amber-800 border-amber-300'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
              )}
            >
              Apagadas ({apagadas.length})
            </button>
          )}
        </div>
      </div>

      {/* Listado */}
      <div className="space-y-3">
        {visible.map(a => (
          <AutomationCard
            key={a.id}
            automation={a}
            estado={estadoDe(a.id)}
            guardando={!!guardando[a.id]}
            bloqueo={bloqueoDe(a.id)}
            errorCambio={erroresCambio[a.id]}
            onCambiar={() => alPulsar(a)}
          />
        ))}
        {visible.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">
            Ninguna automatización coincide con el filtro.
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 leading-relaxed">
        Este listado se mantiene a mano a partir de <span className="font-mono">function_app.py</span> del repositorio{' '}
        <span className="font-mono">{INFRA.repo}</span>, revisado por última vez el{' '}
        {formatReviewDate(LAST_REVIEWED)} (commit <span className="font-mono">{LAST_REVIEWED_COMMIT}</span>).
        Al añadir, quitar o reprogramar un trigger allí, actualiza{' '}
        <span className="font-mono">client/src/data/automations.ts</span> y su constante{' '}
        <span className="font-mono">LAST_REVIEWED</span>, y la lista de interruptores de{' '}
        <span className="font-mono">server/src/services/automatizaciones.ts</span>.
      </p>

      <Modal
        isOpen={confirmando !== null}
        onClose={() => setConfirmando(null)}
        title={confirmando ? `¿Apagar «${confirmando.name}»?` : ''}
        size="sm"
      >
        {confirmando && (
          <div className="space-y-4">
            {confirmando.whenOff && <p className="text-sm text-slate-700 leading-relaxed">{confirmando.whenOff}</p>}
            <p className="text-xs text-slate-500 leading-relaxed">
              Deja de hacer nada desde{' '}
              {confirmando.kind === 'timer'
                ? `su siguiente ejecución (${confirmando.when.toLowerCase()})`
                : 'la próxima vez que la llamen'}
              , hasta que alguien la vuelva a encender aquí. El cambio se avisa en el topic Sistemas de Telegram.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmando(null)} className="btn-ghost">Cancelar</button>
              <button
                onClick={() => {
                  const a = confirmando;
                  setConfirmando(null);
                  void cambiar(a, false);
                }}
                className="btn-danger"
              >
                Apagar
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
